import { useMemo } from 'react';
import type { TableStatus } from '@lmac/shared';

/**
 * The hall floor plan, shared by the vendor app and the club console.
 *
 * It is one SVG with a rect per table over the building's architectural plan.
 * Both are drawn in the same coordinate space the club's CorelDRAW file used
 * (1000 units = 1 foot), so nothing needs aligning — see
 * scripts/import_floorplan.py.
 *
 * Deliberately presentational: it takes tables and a selection and reports
 * clicks. It never reads Firestore and never knows a price. That is what lets
 * the vendor map and the admin map be the same component.
 */

export interface MapTable {
  id: string;
  label: string;
  x: number;
  y: number;
  w: number;
  h: number;
  status: TableStatus;
  nearPower: boolean;
}

export interface MapRect {
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface HallMapProps {
  tables: readonly MapTable[];
  width: number;
  height: number;
  /** Table ids the current vendor has chosen. */
  selected?: ReadonlySet<string>;
  /** Omit to render a read-only map — nothing becomes clickable. */
  onToggle?: (id: string) => void;
  background?: { href: string; x: number; y: number; w: number; h: number };
  outlets?: readonly MapRect[];
  showOutlets?: boolean;
  /** Rendered as dashed outlines; not selectable. */
  checkInTables?: readonly MapRect[];
  /**
   * Visible window in map coordinates. Zooming and panning work by moving this
   * rather than by CSS transform, which keeps strokes crisp at every scale and
   * keeps click targets exactly where they look.
   */
  view?: { x: number; y: number; w: number; h: number };
}

/**
 * Fill and stroke per state.
 *
 * `held` and `sold` deliberately differ now. They once shared a grey so the
 * map would say only "not available" — but three greys side by side read as
 * one, and a vendor could not see what was gone. The distinction leaks nothing
 * personal (no name, no reason) and tells them something worth knowing: an
 * amber table is in somebody's cart or waiting on a check, and may come free;
 * a red one is paid for and will not.
 */
const PAINT: Record<TableStatus, { fill: string; stroke: string; text: string }> = {
  available: { fill: 'var(--map-free)', stroke: 'var(--map-free-line)', text: 'var(--map-free-ink)' },
  held: { fill: 'var(--map-pending)', stroke: 'var(--map-pending-line)', text: 'var(--map-pending-ink)' },
  sold: { fill: 'var(--map-gone)', stroke: 'var(--map-gone-line)', text: 'var(--map-gone-ink)' },
  blocked: { fill: 'var(--map-off)', stroke: 'var(--map-off-line)', text: 'var(--map-off-ink)' },
};

const SELECTED = { fill: 'var(--map-pick)', stroke: 'var(--map-pick)', text: 'var(--map-pick-ink)' };

export function HallMap({
  tables,
  width,
  height,
  selected,
  onToggle,
  background,
  outlets,
  showOutlets = true,
  checkInTables,
  view,
}: HallMapProps) {
  /*
   * Label size is fixed in MAP units, so it scales with the drawing.
   *
   * It was briefly derived from the visible window instead, on the theory that
   * labels should stay a constant size on screen. That is exactly wrong: a
   * constant screen size means the numbers do not grow when you zoom, so the
   * tables get bigger underneath them and the labels read as shrinking. On a
   * phone they sat at about 5px at every zoom level, which is unreadable, and
   * made zooming in pointless for the one thing people zoom in to do.
   *
   * Fixed in map units, a label doubles when the map doubles — 4.8px fitted on
   * a 400px phone, 19px at 4x, which is what zooming is for.
   */
  const fontSize = useMemo(() => Math.round(width / 84), [width]);

  return (
    <svg
      className="hallmap"
      viewBox={
        view
          ? `${view.x} ${view.y} ${view.w} ${view.h}`
          : `0 0 ${width} ${height}`
      }
      role={onToggle ? 'application' : 'img'}
      aria-label={
        onToggle
          ? 'Floor plan. Choose your tables.'
          : 'Floor plan showing which tables are still available.'
      }
    >
      {background && (
        <image
          href={background.href}
          x={background.x}
          y={background.y}
          width={background.w}
          height={background.h}
          preserveAspectRatio="none"
          opacity="var(--map-plan-opacity)"
          filter="var(--map-plan-filter)"
        />
      )}

      {showOutlets && outlets?.map((o, i) => (
        <rect
          key={`outlet-${i}`}
          x={o.x} y={o.y} width={o.w} height={o.h} rx={200}
          fill="var(--map-power)" opacity="0.85"
        >
          <title>Electrical outlet</title>
        </rect>
      ))}

      {checkInTables?.map((c, i) => {
        // Vertical ones are only ~2.5ft wide, so the label is turned to run
        // along the table — which is how the club's own plan draws it.
        const vertical = c.h > c.w;
        const cx = c.x + c.w / 2;
        const cy = c.y + c.h / 2;
        return (
          <g key={`checkin-${i}`} className="hallmap__checkin">
            <title>Check-in table</title>
            <rect
              x={c.x} y={c.y} width={c.w} height={c.h} rx={250}
              fill="var(--map-checkin)"
              stroke="var(--map-checkin-line)"
              strokeWidth={260}
            />
            <text
              x={cx}
              y={cy}
              textAnchor="middle"
              dominantBaseline="central"
              fontSize={Math.round(fontSize * 0.62)}
              fontFamily="ui-monospace, SFMono-Regular, Menlo, monospace"
              fontWeight={600}
              fill="var(--map-checkin-ink)"
              pointerEvents="none"
              transform={vertical ? `rotate(-90 ${cx} ${cy})` : undefined}
            >
              Check-in
            </text>
          </g>
        );
      })}

      {tables.map((t) => {
        const isSelected = selected?.has(t.id) ?? false;
        const selectable = Boolean(onToggle) && (t.status === 'available' || isSelected);
        const paint = isSelected ? SELECTED : PAINT[t.status];

        const describe = isSelected
          ? `Table ${t.label}, chosen`
          : t.status === 'available'
            ? `Table ${t.label}${t.nearPower ? ', near an electrical outlet' : ''}`
            : t.status === 'blocked'
              ? `Table ${t.label}, not for sale`
              : `Table ${t.label}, already taken`;

        return (
          <g
            key={t.id}
            className={`hallmap__table${selectable ? ' is-selectable' : ''}${isSelected ? ' is-selected' : ''}`}
            role={selectable ? 'checkbox' : undefined}
            aria-checked={selectable ? isSelected : undefined}
            aria-label={describe}
            tabIndex={selectable ? 0 : undefined}
            onClick={selectable ? () => onToggle?.(t.id) : undefined}
            onKeyDown={
              selectable
                ? (e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      onToggle?.(t.id);
                    }
                  }
                : undefined
            }
          >
            <title>{describe}</title>
            <rect
              className="hallmap__body"
              x={t.x} y={t.y} width={t.w} height={t.h} rx={250}
              fill={paint.fill} stroke={paint.stroke} strokeWidth={220}
            />
            <text
              x={t.x + t.w / 2}
              y={t.y + t.h / 2}
              textAnchor="middle"
              dominantBaseline="central"
              fontSize={fontSize}
              fontFamily="ui-monospace, SFMono-Regular, Menlo, monospace"
              fontWeight={500}
              fill={paint.text}
              pointerEvents="none"
            >
              {t.label}
            </text>
            {t.nearPower && (
              <circle
                cx={t.x + t.w - 700}
                cy={t.y + 700}
                r={380}
                fill="var(--map-power)"
                pointerEvents="none"
              />
            )}
          </g>
        );
      })}
    </svg>
  );
}
